import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import BottomNav from "@/components/layout/BottomNav";
import AppTopBar from "@/components/layout/AppTopBar";
import { getCurrentSocio, logoutSocio } from "@/data/socioAuthStore";
import { api } from "@/services/api";

/**
 * Layout del portal socio con guarda fail-closed.
 *
 * 1. Sin snapshot local → redirect a /login.
 * 2. Con snapshot → estado `checking`: placeholder neutro hasta que
 *    `/api/socios/auth/me` responda 200. NUNCA se renderiza el shell
 *    del socio (inicio, boletos, perfil) durante la verificación.
 * 3. Cualquier error (401/403/network) → fail-closed: se limpia
 *    snapshot y redirige a /login.
 */
export default function AppLayout() {
  const location = useLocation();
  const [state, setState] = useState(
    getCurrentSocio() ? "checking" : "denied",
  );
  // Altura real de la barra inferior fija (incluye safe-area-inset-bottom
  // porque el <nav> aplica `pb-safe`). Se mide en runtime para que el
  // contenido desplazable nunca quede tapado, en cualquier alto de ventana,
  // zoom o vista móvil. Solución centralizada: ninguna pantalla parcha esto.
  const [navH, setNavH] = useState(96);

  useLayoutEffect(() => {
    if (state !== "allowed") return;
    const el = document.querySelector('[data-testid="app-bottom-nav"]');
    if (!el) return;
    const medir = () => setNavH(el.offsetHeight || 96);
    medir();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(medir);
      ro.observe(el);
    }
    window.addEventListener("resize", medir);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", medir);
    };
  }, [state]);

  useEffect(() => {
    let cancelled = false;
    if (state !== "checking") return;
    (async () => {
      try {
        await api.get("/socios/auth/me");
        if (!cancelled) setState("allowed");
      } catch (err) {
        logoutSocio();
        if (!cancelled) setState("denied");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state]);

  if (state === "denied") {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }
  if (state === "checking") {
    return (
      <div
        data-testid="socio-guard-checking"
        className="min-h-[100dvh] grid place-items-center bg-background"
      >
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          <span className="text-sm">Verificando sesión…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-[100dvh] bg-background text-foreground flex flex-col">
      <AppTopBar />
      <main
        className="flex-1 w-full max-w-2xl mx-auto px-6 md:px-8 pt-6"
        style={{ paddingBottom: navH + 24 }}
      >
        <Outlet />
      </main>
      <BottomNav />
    </div>
  );
}
